import { yupResolver } from "@hookform/resolvers/yup";
import React from "react";
import { Col, Row } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { Outlet, useParams } from "react-router-dom";
import type { AccountWithHoldingsAndBalances } from "../../../../api/src/accounts/accounts.controller";
import type {
  AccountWORiskAllocation,
  EditableAccount,
} from "../../../../api/src/accounts/accounts.service";
import Loading from "../../Loading";
import FormError from "../../components/FormError";
import SettlementWarning from "../../components/SettlementWarning";
import TabContainerWithTabs from "../../components/TabContainer";
import { schema as billingSchema } from "./AccountBillingInfo";
import AccountHeader from "./AccountHeader";
import { schema as tradingSchema } from "./AccountModelTrading";
import AccountNav from "./AccountNav";
import { schema as profileSchema } from "./AccountProfile";
import { useQueryAccount } from "./lib";

/**
 * The account page holds what `GET /accounts/:id?balances=true` sends
 * (`AccountWithHoldingsAndBalances`). `AccountTax.tsx`, `AccountTaxSummary.tsx`
 * and `AccountModelTrading.tsx` also read `unrealizedGain`, `unrealizedLoss`
 * and `performanceYTD`, which that route does not send: only
 * `GET /households/:id?accounts=true` attaches them
 * (`AccountWORiskAllocation`). They are optional here, and absent on this page,
 * so those fields show 0.
 */
export const AccountContext = React.createContext<{
  account?: AccountWithHoldingsAndBalances &
    Partial<
      Pick<
        AccountWORiskAllocation,
        "unrealizedGain" | "unrealizedLoss" | "performanceYTD"
      >
    >;
}>({});

const AccountInfo = () => {
  const form = useForm<EditableAccount>({
    mode: "onBlur",
    resolver: yupResolver(
      billingSchema.concat(tradingSchema).concat(profileSchema),
    ),
  });

  const { accountId } = useParams();
  const {
    isPending: isPendingAccount,
    isError: isErrorAccount,
    data: account,
  } = useQueryAccount(parseInt(accountId ?? ""), {
    includeBalances: true,
  });

  return isPendingAccount ? (
    <Loading />
  ) : isErrorAccount ? (
    <FormError message="Failed to load account" />
  ) : typeof account === "undefined" ? (
    <FormError message="Account not found" />
  ) : (
    <AccountContext.Provider value={{ account }}>
      <AccountHeader />
      <Row className="mb-3">
        <Col>
          <SettlementWarning />
        </Col>
      </Row>
      <FormProvider {...form}>
        <Row>
          <Col>
            <TabContainerWithTabs tabs={AccountNav}>
              <Col>
                <Outlet />
              </Col>
            </TabContainerWithTabs>
          </Col>
        </Row>
      </FormProvider>
    </AccountContext.Provider>
  );
};

export default AccountInfo;
